import { BigNumber } from 'bignumber.js'
import { isEmptyOrNil } from '.'
import math from '@/utils/math'

/**
 * @description: 小数位强制补零 - 内部方法
 * @return {*}
 * @param {string} _numStr 数字字符串
 * @param {number} decimalPlaces 小数位
 */
const forceZeroPadding = (_numStr: string, decimalPlaces: number): string => {
  const dotIndex = _numStr.indexOf('.')
  let currentDecimalPlaces = 0
  if (dotIndex === -1) {
    _numStr += '.'
    currentDecimalPlaces = 0
  } else {
    currentDecimalPlaces = _numStr.length - dotIndex - 1
  }
  const neededZeroes = Math.max(0, decimalPlaces - currentDecimalPlaces)
  // 按位数补零
  return _numStr.padEnd(_numStr.length + neededZeroes, '0')
}

/**
 * 格式化数字为字符串，可指定最小小数位和最大小数位或强制补零，会做数字校验
 * @param {number | string} value - 要格式化的数字。
 * @param {Object} options - 配置参数。
 * @param {'blur' | null | 'input' | 'format' } [options.event]
 * - 格式化事件,
 * - null | blur - 默认值，用于@blur，不做特殊处理
 * - input - 实时更新事件：将取消部分输入补充、文本化事件；且options.placesMin会失效，避免用户无法输入
 * - format - 文本化展示：支持对0的格式化处理
 * @param {number | null } [options.placesMin] - 最小小数位数, 不足的小数位会强制补零。【input事件无效】
 * @param {number | null } [options.placesMax] - 最大小数位数 - 【该值为0时不允许输入小数】。
 * @param {number | null } [options.valueMin] - 格式化数字最小数值 【input事件无效, 建议@blur处理】
 * @param {number | null } [options.valueMax] - 格式化数字最大数值
 * @param {number | null } [options.intMax] - 最大整数位数
 * @param {boolean | null } [options.abs] - 绝对值，仅允许输入>=0的数字
 * @param {boolean | null } [options.nonZero] - 是否不允许为0，如为0则输出空 【input事件无效】
 * @param {boolean | null } [options.forceZero] - 是否根据最大小数位强制补零  【input事件无效】【format事件，会对0补零】
 * @param {boolean | null } [options.thousands] - 千分位  【input事件无效】
 * @param {boolean | null } [options.plusSign] - 正数+正号，【input事件无效】
 * @returns {string} 格式化后的数字字符串。
 */
export const formatDecimal = (
  value: number | string,
  options: {
    event?: 'blur' | null | 'input' | 'format'
    placesMin?: number | null
    placesMax?: number | null
    valueMin?: number | null
    valueMax?: number | null
    intMax?: number | null
    abs?: boolean | null
    nonZero?: boolean | null
    forceZero?: boolean | null
    thousands?: boolean | null
    plusSign?: boolean | null
  } = {},
): string => {
  /* 数字校验与过滤, 仅过滤错误值，如果是111,11aa.cc.1 会转换 11111.1 */
  if ([null, undefined, NaN, Infinity, ''].includes(value)) {
    return ''
  }
  /* 数字合法性过滤 */
  // 科学计数法
  let notationRegex = /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/
  let numberString = notationRegex.test(String(value))
    ? new BigNumber(value).toFormat({ decimalSeparator: '.' })
    : String(value)
  numberString = numberString.replace(/[^-0-9.]+/g, '')

  // 0格式化
  if (Number(numberString) === 0) {
    if (options.event !== 'input') {
      // 如-0，0.  input event时无效
      numberString = '0'
    } else if (options.event === 'input' && numberString === '00') {
      // 如00时
      numberString = '0'
    }
  }

  // 首字符为0时，后面只能接小数点
  let zeroIndex = numberString.indexOf('0')
  let dotIndex = numberString.indexOf('.')
  if (zeroIndex === 0) {
    if (dotIndex !== -1) {
      numberString = '0' + numberString.substring(dotIndex)
    } else if (numberString !== '0') {
      // 整数前面不允许为0
      numberString = numberString.replace(/^0+/, '')
    }
  }

  // 替换多余的-
  const minusIndex = numberString.indexOf('-')

  if (minusIndex !== -1) {
    const minusText = minusIndex === 0 ? '-' : ''
    numberString = minusText + numberString.replace(/-/g, '')
    numberString = numberString.replace(/-\./g, '-')
    if (numberString === '-' && options.event !== 'input') {
      numberString = ''
    }
  }

  // 小数点相关
  if (numberString.includes('.')) {
    // 首字符为. 替换单个.
    let dotIndex = numberString.indexOf('.')
    if (dotIndex === 0) {
      numberString = numberString.replace('.', '')
    }
    // 过滤多个.
    dotIndex = numberString.indexOf('.')
    let numStr1 = numberString.substring(0, dotIndex + 1)
    let numStr2 = numberString.substring(dotIndex).replace(/\./g, '')
    // 小数位值为0时去除
    if (options.event !== 'input') {
      numStr2 = Number(numStr2) === 0 ? '' : numStr2
    }
    numberString = numStr1 + numStr2
    // 尾字符为.去除
    dotIndex = numberString.indexOf('.')
    if (dotIndex === numberString.length - 1 && options.event !== 'input') {
      numberString = numberString.replace(/\./g, '')
    }
  }
  // 取绝对值，不允许负数
  if (options.abs) {
    numberString = numberString.replace(/-/g, '')
  }

  /* ============ return 判断 ========== */
  // 正确性校验 非数字不处理格式化逻辑
  if (!new BigNumber(numberString).isFinite()) {
    return numberString
  }

  let bigNumberValue = new BigNumber(numberString)
  // 格式化数字最小数值 input event时无效
  if (bigNumberValue.lt(String(options.valueMin)) && options.event !== 'input') {
    numberString = String(options.valueMin)
  }

  // 格式化数字最大数值
  if (bigNumberValue.gt(String(options.valueMax))) {
    numberString = String(options.valueMax)
  }

  // 整数位限制，截取整数位数
  if (Number(options.intMax) > 0) {
    let dotIndex = numberString.indexOf('.')
    let intNumberString = ''
    let decimalNumberString = ''
    if (dotIndex !== -1) {
      intNumberString = numberString.substring(0, dotIndex)
      decimalNumberString = numberString.substring(dotIndex)
    } else {
      intNumberString = numberString
    }
    intNumberString = intNumberString.substring(0, Number(options.intMax))
    numberString = intNumberString + decimalNumberString
  }

  // 小数位处理逻辑，截取小数位数
  if (Number(options.placesMax) === 0) {
    // 最大小数位为0，不允许输入小数，不处理最小小数位
    numberString = numberString?.split('.')?.[0]
  } else {
    // 最大小数位限制，截取小数位数
    if (Number(options.placesMax) > 0) {
      let dotIndex = numberString.indexOf('.')
      if (dotIndex !== -1) {
        numberString = numberString.substring(0, dotIndex + Number(options.placesMax) + 1)
        // 格式化，如-0.0001截取小数位后为-0.000，格式化为0,
        // input event时无效
        if (options.event !== 'input') {
          numberString = numberString.replace(/\.?0+$/, '')
          if (Number(numberString) === 0) {
            numberString = numberString.replace(/-/g, '')
          }
        }
      }
    }
    // 最小小数位处理 input event时无效
    if (Number(options.placesMin) > 0 && options.event !== 'input') {
      // 不足小数位补零
      numberString = forceZeroPadding(numberString, Number(options.placesMin))
    }
  }

  /* 文本化处理 */
  // 文本化前真实的值
  const bigNumberConstValue = new BigNumber(numberString)

  /* ============ return 判断 =========== */
  // 不允许为0时，返回空，input event时无效
  if (bigNumberConstValue.isZero() && options.nonZero && options.event !== 'input') {
    return ''
  }
  // 为0时，后续格式化逻辑不处理， format事件除外
  if (numberString === '0' && options.event !== 'format') {
    return '0'
  }

  // 是否按最大小数位强制补零
  if (options.forceZero && Number(options.placesMax) > 0 && options.event !== 'input') {
    numberString = forceZeroPadding(numberString, Number(options.placesMax))
  }

  // 千分位格式化 input event时无效
  if (options.thousands && options.event !== 'input') {
    let numberArr = numberString.split('.')
    let intNumberString = numberArr[0]
    let decimalNumberString = numberArr[1] ? '.' + numberArr[1] : ''
    intNumberString = intNumberString.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    numberString = intNumberString + decimalNumberString
  }

  // 正数是否补充正号
  if (options.plusSign && options.event !== 'input') {
    // 大于0
    if (bigNumberConstValue.gt(0)) {
      numberString = `+${numberString}`
    }
  }
  return numberString
}

// input函数格式化方法  同formatDecimal
export const inputFormatDecimal = (
  value: number | string,
  options: {
    event?: 'blur' | null | 'input' | 'format'
    placesMin?: number | null
    placesMax?: number | null
    valueMin?: number | null
    valueMax?: number | null
    intMax?: number | null
    abs?: boolean | null
    nonZero?: boolean | null
    forceZero?: boolean | null
    thousands?: boolean | null
    plusSign?: boolean | null
  } = {},
): string => {
  return formatDecimal(value, { event: 'input', ...options })
}

/**
 * @description: 数字格式化 - 指定小数位
 * @return {*}
 * @param {number | string} value 值
 * @param {number} decimal 保留小数位
 * @param {boolean} options.forceZero 强制补零 - 默认false
 * @param {boolean} options.thousands 千分位 - 默认false
 */
export const formatDecimalPlaces = (
  value: number | string,
  decimal: number,
  {
    forceZero,
    thousands,
  }: {
    forceZero?: boolean
    thousands?: boolean
  },
): string => {
  forceZero = forceZero !== undefined ? forceZero : false
  thousands = thousands !== undefined ? thousands : false
  return formatDecimal(value, {
    event: 'format',
    placesMax: decimal,
    forceZero,
    thousands,
  })
}

/**
 * 数字转百分比 - 兼容bigNumber，正数带+
 * @param { number } num 值
 * @param { object } options - 配置参数。
 * @param { number } options.decimal // 小数位 默认2位
 * @param { boolean } options.forceZero // 是否根据小数位补零 默认true
 * @param { boolean } options.plusSign // 正数是否带正号 默认带
 * @param { string | number } options.errValue // 缺省值 默认为--
 * @return String 如 0%
 */
export const formatterPercentNum = (
  num: number | string,
  {
    decimal,
    forceZero,
    plusSign,
    errValue,
  }: {
    decimal?: number
    forceZero?: boolean
    plusSign?: boolean
    errValue?: string | number
  } = {},
): string | number => {
  // 默认值相关
  decimal = decimal !== undefined ? decimal : 2
  forceZero = forceZero !== undefined ? forceZero : true
  plusSign = plusSign !== undefined ? plusSign : true
  errValue = errValue || '-'
  // 正确性校验
  let bigNumberValue = new BigNumber(num)
  let result = ''
  if (bigNumberValue.isFinite()) {
    // * 100
    bigNumberValue = bigNumberValue.times(100)
    result = bigNumberValue.toFormat()
    if (decimal) {
      // 补零
      result = forceZeroPadding(result, decimal)
      // 截取小数位
      let dotIndex = result.indexOf('.')
      if (dotIndex !== -1) {
        result = result.substring(0, dotIndex + decimal + 1)
      }
    } else {
      result = result.split('.')[0]
    }
    // 大于0 加+号
    if (plusSign && bigNumberValue.gt(0)) {
      result = `+${result}`
    }
    return `${result}%`
  } else {
    return errValue
  }
}

/**
 *  @description 数字转百分比 ———— 轻量版，补零，不带正号，保留2位小数
 *  @params {number} num 数值
 *  @params {number} decimal 百分比需保留的小数位 -
 *  @return {string | number} 保留指定小数位后的值 或者0
 * */
export const num2Percent = function (
  num: number | string | null | undefined,
  decimal: number = 2,
  errValue: number | string = 0,
): string | number {
  if (isEmptyOrNil(num)) return '-'
  return formatterPercentNum(num as number, {
    decimal,
    forceZero: true,
    plusSign: false,
    errValue,
  })
}

// 定义格式化参数类型
interface FormatParams {
  decimal?: number
  thousands?: boolean
}

// 定义返回结果类型
interface FormatResult {
  denom: string
  amount: string
}

// BigNumber 格式化配置
const FORMAT_OPTIONS = {
  decimalSeparator: '.',
  groupSeparator: ',',
  groupSize: 3,
}

interface FormattedNumberProps {
  num: number | string
  units?: string[]
  precision?: number
  withCommas?: boolean
}

interface FormattedNumberReturn {
  number: number | string
  unit: string | null
}
export function formatNumberWithUnits({
  num,
  units = ['M', 'B'],
  precision = 0,
  withCommas = false,
}: FormattedNumberProps): FormattedNumberReturn {
  // 将字符串类型的 num 转换成 BigNumber
  const number = new BigNumber(num)

  let unitIndex = -1

  // 确定单位
  for (let i = 0; i < units.length; i++) {
    if (i === 0 && number.isGreaterThanOrEqualTo(1000000) && number.isLessThan(1000000000)) {
      unitIndex = 0
      break
    } else if (i === 1 && number.isGreaterThanOrEqualTo(1000000000)) {
      unitIndex = 1
      break
    }
  }

  // 格式化数值
  if (unitIndex !== -1) {
    const formattedNum = number.dividedBy(new BigNumber(1000).pow(unitIndex + 2)).toFixed(precision)
    return {
      number: withCommas ? `${formatNumberWithCommas(formattedNum)}` : formattedNum,
      unit: units[unitIndex],
    }
  } else {
    return {
      number: withCommas ? `${formatNumberWithCommas(number.toString())}` : num,
      unit: null,
    }
  }
}

interface FormatNumberWithCommasOptions {
  decimal?: number
}

export function formatNumberWithCommas(
  num: number | string,
  options: FormatNumberWithCommasOptions = { decimal: 2 },
): string {
  const { decimal } = options

  // 使用 BigNumber 处理数值
  const number = new BigNumber(num)

  // 格式化数值，保留指定的小数位
  return number.toFormat(decimal!, BigNumber.ROUND_DOWN)
}

/**
 * @description 两个数字相加
 * @return {string}
 * @param {string} num1 无单位的字符串
 * @param {string} num2 无单位的字符串
 */
export function numInPlus(num1: string, num2: string): string {
  return new BigNumber(num1).plus(num2).toString()
}

/**
 * @description 两个数字相加
 * @return {string}
 * @param {string} num1 无单位的字符串
 * @param {string} num2 无单位的字符串
 */
export function numInMinus(num1: string, num2: string): string {
  return new BigNumber(num1).minus(num2).toFormat()
}

/**
 * 千数字格式化
 * @param num
 * @returns
 */
export function formatNumber(num: string | number | undefined | null) {
  if (isEmptyOrNil(num))
    return {
      value: num,
      length: 0,
    }

  const parts = num!.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return {
    value: parts.join('.'),
    length: parts.join('.').length,
  }
}

/**
 * 去掉小数点后的零
 * @param num
 * @param n
 * @returns
 */
export function removeTrailingZeros(num: string | number | null | undefined, n: number = 0) {
  if (isEmptyOrNil(num)) return '-'
  // 将数字转换为字符串
  let str = num!.toString()

  // 如果字符串中包含小数点
  if (str.indexOf('.') !== -1) {
    // 移除末尾的零
    str = str.replace(/0+$/, '')
    // 如果小数点后没有数字了，移除小数点
    str = str.replace(/\.$/, '')
  }
  if (n) {
    str = formatDecimalCus(Number(str), n).toString()
  }

  return str
}

export const ugeaToGea2 = (
  ugea: string | number | null | undefined,
  isTrunc: boolean = false,
  keepDecimalNum: number = 8,
) => {
  if (isEmptyOrNil(ugea) || ugea === '-')
    return {
      value: '-',
      sigin: '',
    }
  const ugagNum = `${ugea}`!.replace(/ugea$/, '').replace(/GEA$/, '')
  if (isTrunc) {
    const num = divideByHundredMillion(ugagNum)
    let num2 = removeTrailingZeros(formatDecimalCus(num, 0))
    num2 = new BigNumber(num2).toFormat(FORMAT_OPTIONS)
    return {
      value: num2,
      sigin: 'GEA',
    }
  }
  const num = divideByHundredMillion(ugagNum)
  let num2 = removeTrailingZeros(formatDecimalCus(num, keepDecimalNum))
  num2 = new BigNumber(num2).toFormat(FORMAT_OPTIONS)

  return {
    value: num2,
    sigin: 'GEA',
  }
}

/**
 * @description: ugea 字符串转换为 gea
 * @return {string}
 * @param {string | number} ugeaStr
 */
export const ugeaToGea3 = (value: string | number | null | undefined, denom: string) => {
  if (isEmptyOrNil(value))
    return {
      value: '-',
      sigin: '',
    }
  const demonNum = `${value}${denom}`
  if (denom === 'gea') {
    const value = geaStrToGea(demonNum)
    return value
  } else {
    const value = ugeaToGea2(demonNum)
    return value
  }
}

/**
 * @description: gea 字符串转换为 gea
 * @return {string}
 * @param {string | number} geaStr
 */
export const geaStrToGea = (geaStr: string | number | null | undefined) => {
  if (isEmptyOrNil(geaStr))
    return {
      value: '-',
      sigin: '',
    }
  const value = `${geaStr}`!.replace(/gea$/, '')

  return {
    value: formatValue(value, true),
    sigin: 'GEA',
  }
}

/**
 * @description: 格式化值
 * @return {string}
 * @param {string | number} value
 */
export function formatValue(
  value: number | string | null | undefined,
  isThousandth: boolean = false,
) {
  if (isEmptyOrNil(value) || value === '-') return '-'
  if (isThousandth) {
    return new BigNumber(value!).toFormat(FORMAT_OPTIONS)
  }
  return value as string
}

export const ansToGAG = (ans: string | number | null | undefined, isTrunc: boolean = true) => {
  if (isEmptyOrNil(ans)) {
    return '-'
  }
  const value = math.multiply(ans, 160000)
  if (isTrunc) {
    return formatValue(Math.trunc(parseFloat(value)), true)
  } else {
    return formatValue(removeTrailingZeros(formatDecimalCus(value, 8)), true)
  }
}

export const ugagToGag = (
  str: number | string | null | undefined,
  trunc: boolean = false,
  keepDecimalNum: number = 8,
) => {
  if (isEmptyOrNil(str) || str === '-')
    return {
      value: '-',
      sigin: '',
    }

  const ugagNum = `${str}`!.replace(/ugag$/, '')
  if (trunc) {
    const num = new BigNumber(
      Math.trunc(new BigNumber(ugagNum!).div(`100000000`) as unknown as number),
    ).toFormat(FORMAT_OPTIONS)
    return {
      value: num,
      sigin: 'GAG',
    }
  }

  const num = divideByHundredMillion(ugagNum)
  let num2 = removeTrailingZeros(formatDecimalCus(num, keepDecimalNum))
  num2 = new BigNumber(num2).toFormat()
  return {
    value: num2,
    sigin: 'GAG',
  }
}

export const gagStrToGga = (geaStr: string | number | null | undefined) => {
  if (isEmptyOrNil(geaStr))
    return {
      value: '-',
      sigin: '',
    }
  return {
    value: `${geaStr}`!.replace(/gea$/, ''),
    sigin: 'GAG',
  }
}

export const ugagToGag2 = (value: string | number | null | undefined, denom: string) => {
  if (isEmptyOrNil(value))
    return {
      value: '-',
      sigin: '',
    }
  const demonNum = `${value}${denom}`
  if (denom === 'gag') {
    const value = gagStrToGga(demonNum)
    return value
  } else {
    const value = ugagToGag(demonNum)
    return value
  }
}

/**
 * @description: 格式化小数,保留小数位
 * @return {string}
 * @param {number} num
 * @param {number} n
 */
export function formatDecimalCus(input: number | string, precision: number) {
  if (isEmptyOrNil(input)) return '-'
  // 验证输入
  if (typeof input !== 'number' && typeof input !== 'string') {
    throw new TypeError('Input must be a number or a string representing a number')
  }

  // 使用 BigNumber 解析输入（支持字符串和数字）
  let num
  try {
    num = new BigNumber(input).toFormat(FORMAT_OPTIONS).replace(/,/g, '')
  } catch (e) {
    throw new TypeError('Input must be a valid number')
  }

  // 验证精度
  if (!Number.isInteger(precision) || precision < 0) {
    throw new TypeError('Precision must be a non-negative integer')
  }

  // 处理整数情况
  if (precision === 0) {
    const value = num?.toString()?.split('.')
    return value?.[0] || '0'
  }

  // 设置小数位数并四舍五入
  const valueArr = num.toString().split('.')
  const value1 = valueArr[0]
  if (valueArr.length === 1) {
    return value1
  } else {
    let value2 = valueArr?.[1]?.slice(0, precision)
    value2 = value2?.replace(/0+$/, '')
    return `${value1}.${value2}`
  }
}

// 除以1亿
export function divideByHundredMillion(num: number | string) {
  if (isEmptyOrNil(num)) return '-'
  // const value = +num
  // if (typeof value !== 'number') return '-'
  // // 将数字转换为精确的十进制字符串表示
  // const result = (value / 100000000).toFixed(20)

  // // 移除末尾多余的零和小数点
  // return result.replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1')
  // 创建BigNumber对象
  const bn = new BigNumber(num)
  // 除以1亿 (100,000,000)
  const divided = bn.dividedBy(1e8)
  // 转换为字符串并移除末尾的零和小数点
  let str = divided.toString()
  // 如果有小数点
  if (str.indexOf('.') !== -1) {
    // 移除末尾的零
    str = str.replace(/0+$/, '')
    // 如果小数点后没有数字了，移除小数点
    if (str.endsWith('.')) {
      str = str.substring(0, str.length - 1)
    }
  }
  return str
}

export function getValueByPathOne(obj: any, path: string) {
  try {
    const defaultReturn = { value: '-', key: path }

    if (!obj || typeof obj !== 'object' || !path || typeof path !== 'string') {
      return defaultReturn
    }

    // 将路径字符串转换为路径数组
    const pathSegments = path.split('/').filter((segment) => segment !== '')

    let current = obj

    // 1. 先找到目标值
    for (const segment of pathSegments) {
      // const arrayMatch = segment.match(/^([^\[]+)\[(\d+)\]$/)
      const arrayMatch = segment.match(/^([^[]+)\[(\d+)\]$/)

      if (arrayMatch) {
        const key = arrayMatch[1]
        const index = parseInt(arrayMatch[2], 10)

        if (current[key] && Array.isArray(current[key]) && current[key][index] !== undefined) {
          current = current[key][index]
        } else {
          return defaultReturn
        }
      } else {
        if (current[segment] !== undefined) {
          current = current[segment]
        } else {
          return defaultReturn
        }
      }
    }

    if (current === undefined) {
      return defaultReturn
    }

    // 2. 处理找到的值
    // 检查是否是数字或字符串数字
    const isNumeric = !isNaN(current) && !isNaN(parseFloat(current))

    if (isNumeric) {
      // 构建demo字段的路径
      const demoPath = [...pathSegments]
      demoPath[demoPath.length - 1] = 'denom'

      // 尝试获取denom字段的值
      let demoValue = obj
      let hasDemo = true

      for (const segment of demoPath) {
        // const arrayMatch = segment.match(/^([^\[]+)\[(\d+)\]$/)
        const arrayMatch = segment.match(/^([^[\]]+)\[(\d+)\]$/)

        if (arrayMatch) {
          const key = arrayMatch[1]
          const index = parseInt(arrayMatch[2], 10)

          if (
            demoValue[key] &&
            Array.isArray(demoValue[key]) &&
            demoValue[key][index] !== undefined
          ) {
            demoValue = demoValue[key][index]
          } else {
            hasDemo = false
            break
          }
        } else {
          if (demoValue[segment] !== undefined) {
            demoValue = demoValue[segment]
          } else {
            hasDemo = false
            break
          }
        }
      }

      if (hasDemo && demoValue !== undefined) {
        return { value: `${current}${demoValue}`, key: path }
      }
      return { value: current, key: path }
    }

    if (typeof current === 'string' && (current.includes('ugea') || current.includes('ugag'))) {
      return { value: current, key: path }
    }

    return current !== undefined ? { value: current, key: path } : defaultReturn
  } catch (e) {
    return { value: '-', key: path }
  }
}
